import axios from "axios";
import { tokenManager } from "./tokenManager";

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL?.trim();
  if (envBase) return envBase;

  // Local dev fallback: when website runs on localhost, use local backend.
  if (typeof window !== "undefined") {
    const host = String(window.location?.hostname || "").toLowerCase();
    if (host === "localhost" || host === "127.0.0.1") {
      return "http://localhost:5001";
    }
  }

  return "https://healapi.healbid.com";
};

const BASE_URL = resolveApiBaseUrl();

/** Patient `/app/*` calls that should not trigger full session teardown on 401 */
const PATIENT_APP_PUBLIC_PREFIXES = [
  "/app/login",
  "/app/signup",
  "/app/send-otp",
  "/app/resend-otp",
  "/app/verify-otp",
  "/app/platform-settings/public",
];

function isPatientAppProtectedUrl(url) {
  const u = String(url || "");
  if (!u.startsWith("/app")) return false;
  return PATIENT_APP_PUBLIC_PREFIXES.every((p) => !u.startsWith(p));
}

function shouldRedirectPatientToLogin() {
  try {
    const p = String(window?.location?.pathname || "").toLowerCase();
    if (p.startsWith("/doctor") || p.startsWith("/hospital")) return false;
    const stay = ["/login", "/signup", "/verify-otp", "/languagepage"];
    if (stay.some((x) => p === x || p.startsWith(`${x}/`))) return false;
    return true;
  } catch (_) {
    return true;
  }
}

/**
 * Deleted / invalid patient session: clear Redux and leave patient pages.
 * Dynamic import avoids circular dependency (Store → thunk → Api → Store).
 */
function handlePatientSessionInvalid(requestUrl) {
  if (!isPatientAppProtectedUrl(requestUrl)) return;

  queueMicrotask(() => {
    Promise.all([
      import("../App/Store"),
      import("../Features/Postes/Slice/authSlice"),
      import("./socket"),
    ])
      .then(([{ store }, { logout }, { resetSocketConnection }]) => {
        try {
          store.dispatch(logout());
        } catch (_) {}
        try {
          resetSocketConnection();
        } catch (_) {}
        if (shouldRedirectPatientToLogin()) {
          try {
            window.location.assign("/Login");
          } catch (_) {}
        }
      })
      .catch(() => {});
  });
}

const API = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

API.interceptors.request.use(
  (config) => {
    const normalizedUrl = tokenManager.normalizeRequestUrl(config?.url);
    config.url = normalizedUrl;
    const token = tokenManager.getTokenForRequest(normalizedUrl);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const isFormData = typeof FormData !== "undefined" && config.data instanceof FormData;

    if (!isFormData && !config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

API.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const normalizedUrl = tokenManager.normalizeRequestUrl(error?.config?.url || "");
    const hadAuth = Boolean(error?.config?.headers?.Authorization);
    const msg = String(error?.response?.data?.message || "").toLowerCase();

    if (status === 401 && hadAuth) {
      tokenManager.clearForRequest(error?.config?.url);
      handlePatientSessionInvalid(normalizedUrl);
    } else if (
      status === 404 &&
      hadAuth &&
      msg.includes("user not found") &&
      isPatientAppProtectedUrl(normalizedUrl)
    ) {
      tokenManager.clearForRequest(error?.config?.url);
      handlePatientSessionInvalid(normalizedUrl);
    }

    return Promise.reject(error);
  }
);

API.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers["Cache-Control"] = "no-cache";
  config.headers.Pragma = "no-cache";
  return config;
});

export const getApiBaseUrl = () => BASE_URL;

export const apiRequest = async ({ method = "GET", url, data, params, headers = {} }) => {
  try {
    const response = await API({
      method,
      url,
      data,
      params,
      headers,
    });

    return response?.data;
  } catch (error) {
    const rawMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Request Failed";
    const isNetworkFailure =
      !error?.response &&
      (rawMessage === "Failed to fetch" || rawMessage === "Network Error");
    const message = isNetworkFailure
      ? "Cannot reach the server. Make sure the backend is running (e.g. npm start in HealBid_Backend) and REACT_APP_API_BASE_URL is correct."
      : rawMessage;

    throw new Error(message);
  }
};

export default API;
